/**
 * Shared base CSS for every thermal-printer slip (customer receipt, KOT,
 * Z-report, PRA tax invoice, purchase order).
 *
 * Important: the `@page` rule MUST declare an explicit width in mm
 * (`size: 80mm auto`, not `size: auto`). Chromium's print dialog filters
 * printers by the document's declared paper size. With `size: auto` it
 * falls back to the OS default (A4/Letter) and thermal printers either
 * disappear from the dropdown or the driver pads each job to the default
 * paper length. Always pass the carriage width (e.g. 80mm) here; the
 * actual rendered content lives inside a narrower `.receipt`/`.thermal-page`
 * container so it never reaches the paper's hard right edge.
 *
 * `lockExactPageSize` in `printPageSize.ts` later appends a SECOND `@page`
 * rule with an explicit measured height (e.g. `size: 80mm 47mm`) so the
 * driver feeds only what the slip needs. Because the later rule wins on
 * the CSS cascade, the auto height is replaced — but the printer-list
 * step in the dialog has already used the initial 80mm width to decide
 * which printers to offer, which is exactly why the explicit width matters.
 */
export type ThermalPrintOpts = {
  /** Physical roll width in mm — the load-bearing value: declared to the driver. */
  paperWidthMm: number
  /** True printable area in mm — width of the visible content frame. */
  printableAreaMm: number
  /** Uniform scale applied to every font-size in the variant stylesheet. */
  fontScale: number
}

export function thermalPrintBaseCss(opts: ThermalPrintOpts): string {
  const paper = Math.max(40, Math.min(112, opts.paperWidthMm))
  const printable = Math.max(40, Math.min(110, opts.printableAreaMm))
  return `
    @page { size: ${paper}mm auto; margin: 0; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #000;
      width: ${paper}mm;
      max-width: ${paper}mm;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .thermal-page {
      width: ${printable}mm;
      max-width: ${printable}mm;
      padding: 2mm 0 5mm;
      margin: 0 auto;
      box-sizing: border-box;
    }
  `
}
